import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { settingsAPI } from '../services/api';
import { Settings as SettingsIcon, Edit2, Trash2, Plus } from 'lucide-react';
import { createNavigationHelpers } from '../utils/navigationHelpers';
import { usePermissions } from '../hooks/usePermissions';
import ConfirmationModal from '../components/ConfirmationModal';

const Settings = () => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [settingToDelete, setSettingToDelete] = useState(null);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { goToCreate, goToEdit } = createNavigationHelpers('settings', navigate);
  const { settings: settingPerms } = usePermissions();

  // Fetch settings
  const { data: settingsData, isLoading, error } = useQuery(
    ['settings'],
    () => settingsAPI.getAll(),
    {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      onSuccess: (data) => {
        console.log('Settings query success:', data);
      },
      onError: (error) => {
        console.error('Settings fetch error:', error);
        toast.error('Failed to load settings');
      }
    }
  );

  // Delete setting mutation
  const deleteMutation = useMutation(
    (uuid) => settingsAPI.delete(uuid),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['settings']);
        toast.success('Setting deleted successfully!');
        setDeleteModalOpen(false);
        setSettingToDelete(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'An error occurred');
      },
    }
  );

  const handleEdit = (setting) => {
    goToEdit(setting.uuid);
  };

  const handleDelete = (setting) => {
    setSettingToDelete(setting);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (settingToDelete) {
      deleteMutation.mutate(settingToDelete.uuid || settingToDelete.id || settingToDelete.key);
    }
  };



  // Debug logging
  console.log('=== SETTINGS DEBUG ===');
  console.log('Raw settingsData:', settingsData);
  console.log('settingsData?.data:', settingsData?.data);
  console.log('settingsData?.data?.data:', settingsData?.data?.data);
  
  // Extract settings from the correct path in the API response
  let settings = [];
  
  // First check if we have the nested structure (data.data.data)
  if (Array.isArray(settingsData?.data?.data)) {
    settings = settingsData.data.data;
    console.log('Using nested data structure for settings');
  } 
  // Then check if we have a simpler structure (data.data)
  else if (Array.isArray(settingsData?.data)) {
    settings = settingsData.data;
    console.log('Using simple data structure for settings');
  }
  // Check if data.data is an object (key-value pairs)
  else if (settingsData?.data?.data && typeof settingsData.data.data === 'object') {
    // Convert object to array
    settings = Object.keys(settingsData.data.data).map(key => ({
      key: key,
      value: settingsData.data.data[key].value,
      description: settingsData.data.data[key].description,
      type: typeof settingsData.data.data[key].value === 'boolean' ? 'boolean' : 
            !isNaN(settingsData.data.data[key].value) ? 'number' : 'text'
    }));
    console.log('Converted object to array for settings');
  }
  
  console.log('Extracted settings:', settings ? settings.length : 0);
  
  // Ensure settings is always an array
  settings = settings || [];

  const getTypeColor = (type) => {
    const colors = {
      text: 'bg-blue-100 text-blue-800',
      number: 'bg-green-100 text-green-800',
      boolean: 'bg-purple-100 text-purple-800',
      json: 'bg-orange-100 text-orange-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const formatValue = (value, type) => {
    if (type === 'boolean') {
      return value === 'true' || value === true ? 'Yes' : 'No';
    }
    if (type === 'json') {
      try {
        return JSON.stringify(JSON.parse(value), null, 2);
      } catch {
        return value;
      }
    }
    return value;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-600 text-lg font-medium mb-2">Failed to load settings</div>
          <div className="text-gray-500 text-sm">Please try refreshing the page</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card-gradient p-4 sm:p-6 text-center rounded-xl animate-fade-in-up">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="text-left">
            <h1 className="text-2xl sm:text-3xl font-bold gradient-text mb-2">Settings</h1>
            <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base">
              Manage system configuration and preferences
            </p>
          </div>
          {/* Add Setting Button - Only show if user can create settings */}
          {settingPerms.canCreate && (
            <button
              onClick={() => {
                console.log('Add Setting button clicked');
                console.log('Navigating to /settings/new');
                navigate('/settings/new');
              }}
              className="btn-primary flex items-center justify-center text-sm sm:text-base px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl shadow-glow hover:scale-105 transition-all duration-300"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Setting
            </button>
          )}
        </div>
      </div>

      {/* Settings Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
        {settings.map((setting, index) => (
          <div
            key={setting.uuid || setting.id || setting.key}
            className="card-modern group hover-lift animate-fade-in-up overflow-hidden"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center text-white shadow-glow group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                <SettingsIcon className="h-5 w-5" />
              </div>
              <span className={`px-2.5 py-1 text-xs font-medium rounded-full whitespace-nowrap ${getTypeColor(setting.type)}`}>
                {setting.type}
              </span>
            </div>
            
            <div className="mb-4">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white truncate mb-2">{setting.key}</h3>
              {setting.description && (
                <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-2">{setting.description}</p>
              )}
            </div>
            
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Value:</p>
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 overflow-hidden">
                <pre className="text-xs sm:text-sm text-gray-900 dark:text-gray-300 whitespace-pre-wrap break-all overflow-x-auto max-h-32 overflow-y-auto">
                  {formatValue(setting.value, setting.type)}
                </pre>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-4">
              <span className="truncate">Updated: {new Date(setting.updated_at).toLocaleDateString()}</span>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {/* Edit Button - Only show if user can update settings */}
              {settingPerms.canUpdate && (
                <button
                  onClick={() => handleEdit(setting)}
                  className="btn-secondary text-sm flex items-center gap-1 px-3 py-2 rounded-lg"
                >
                  <Edit2 className="h-3 w-3 flex-shrink-0" />
                  Edit
                </button>
              )}
              
              {/* Delete Button - Only show if user can delete settings */}
              {settingPerms.canDelete && (
                <button
                  onClick={() => handleDelete(setting)}
                  className="text-danger-600 dark:text-danger-400 hover:text-danger-800 dark:hover:text-danger-300 text-sm font-medium flex items-center gap-1 px-3 py-2 rounded-lg hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors"
                >
                  <Trash2 className="h-3 w-3 flex-shrink-0" />
                  Delete
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {settings.length === 0 && (
        <div className="card-modern text-center py-12 animate-fade-in-up">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center text-white text-2xl mb-4 mx-auto shadow-glow">
            <SettingsIcon className="h-8 w-8" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No settings yet</h3>
          <p className="text-gray-600 dark:text-gray-300 mb-4">Get started by adding your first system setting</p>
          {/* Create Setting Button - Only show if user can create settings */}
          {settingPerms.canCreate && (
            <button
              onClick={() => {
                console.log('Create Setting button clicked');
                navigate('/settings/new');
              }}
              className="btn-primary inline-flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Create Setting
            </button>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSettingToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Setting"
        message={`Are you sure you want to delete the setting "${settingToDelete?.key}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default Settings; 